import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocaleService } from '@app/services/locale/locale.service';
import interpolate from '@app/shared/utils/string/interpolate';
import { AffiliationsService } from '../affiliations/affiliations.service';
import { ConfigService } from '@core/config.service';
import { ExpirableSessionStorageService } from '@app/shared/services/expirable-storage/expirable-session-storage.service';
import { CookieService } from 'ngx-cookie-service';
import get from 'lodash-es/get';

@Injectable({
  providedIn: 'root'
})
export class ResourceBundleService {
  url = '/resource-bundle${watcherSuffix}/${brand}/${locale}/homepage/';
  defaultAffiliation = 'standard';
  defaultProfile = 'looker';
  currentUserInfo;
  useWatcher = false;
  sessionTTL = 60000 * 4; // 4 minutes

  constructor(
    private httpClient: HttpClient,
    private localeService: LocaleService,
    private affiliationService: AffiliationsService,
    private configService: ConfigService,
    private storage: ExpirableSessionStorageService,
    private cookieService: CookieService,
  ) {
    const toggles = this.configService.getValue('toggles');
    this.useWatcher = toggles.useWatcherForHomepageContent;
    if (toggles.resourceBundleStorageTTL) {
      this.sessionTTL = toggles.resourceBundleStorageTTL;
    }
  }

  get(type: 'stories' | 'heroes'): Promise<any> {
    return this.setLocaleCookie().then(() => this.getCachedContent()).then(content => {
      const storyOrderingPreset = content.storyOrderingPresets.presets.A;
      content = this.extractRelevantStoriesData(content, type);
      content = content.sort(function (a, b) {
        const val = parseInt(a.generalPurposeStrings.order, 10) - parseInt(b.generalPurposeStrings.order, 10);
        return val;
      });
      return {[type]: content, storyOrderingPreset};
    });
  }

  /**
   * Ping the app domain to set the locale cookie. This is to fix to fix US content
   * showing in UK on first page load where locale cookie isn't immediately set. Intl
   * project should fix this since aka locale cookie is set on initial page load. This
   * project is on hold and only set in latest.
   */
  setLocaleCookie(): Promise<any> {
    const localeCookieNames = this.configService.getValue('localeCookieNames');
    const akaLangCookie = get(localeCookieNames, 'akamai.languageSelection');
    const defaultLangCookie = get(localeCookieNames, 'default.languageSelection');
    const isDev = this.configService.getValue('environment') === 'development';

    if (this.cookieService.get(akaLangCookie) || this.cookieService.get(defaultLangCookie) || isDev) {
      return Promise.resolve(true);
    } else {
      return this.httpClient.get<any>('/authentication/get-client-token/').toPromise();
    }
  }

  private getCachedContent(): Promise<any> {
    console.log('rb: getCachedContent');
    return this.getURL().then(url => {
      console.log('rb:', url);
      const cache = this.storage.getItem(url);
      return this.affiliationService.getAffiliations().then(affiliations => {
        this.extractUserInfo(affiliations);
        if (cache) {
          return Promise.resolve(JSON.parse(cache));
        } else {
          return this.httpClient.get<any>(url).toPromise().then(content => {
            // old homepage didn't have an expire for non-watcher service, but we'll do
            // it for both here since all brands will eventually move to watcher anyway
            const expireTime = new Date().getTime() + this.sessionTTL; // 4 mins in the future
            const expireIsoDate = new Date(expireTime).toISOString();
            this.storage.setItem(url, JSON.stringify(content), expireIsoDate);
            return content;
          });
        }
      });
    });
  }

  private getURL() {
    console.log('rb: getURL()');
    return this.localeService.getLocale().then(locale => {
      locale = locale || 'en';

      const brand = this.configService.getValue('siteId');
      const watcherSuffix = this.useWatcher ? '-watcher' : '';

      // en_US is not supported on dcl, forcing en
      // TODO: convert to config-based
      if (brand === 'dcl' && locale === 'en_US') {
        locale = 'en';
      }

      // TEMP
      if (brand === 'hkdl' && locale === 'en') {
        locale = 'en_US';
      }

      let url = interpolate(this.url, { brand, locale, watcherSuffix });

      // set cache to last 5 minutes = 300000 ma
      const date = new Date();
      const dateTime = Math.round(date.getTime() / 300000);
      url = url + '?time=' + dateTime;
      console.log('rb: url', url);
      return url;

    });
  }

  /*
   * Filter content based on the current user's affiliation(s)/profile(s)
   * @param {Object} resource bundle content
   * @param {String} content type to return (heroes|stories)
   * @returns {Array} filtered stories content
   */
  extractRelevantStoriesData(dataBundle, dataSet) {
    const currentUserAffiliations = this.currentUserInfo.affiliations;
    let chosenAffiliation;

    /* If there are more than 1 affiliations for the current user choose higher priority affiliation
      * higher index means higher priority
      */
    if (dataBundle.affiliationPriority &&
        dataBundle.affiliationPriority.length > 1 &&
        currentUserAffiliations.length > 1) {
        let currentPriority = -99;
        for (let j = 0; j < currentUserAffiliations.length; j++) {

            const checkAffil = currentUserAffiliations[j].toLowerCase(),
                checkIndex = dataBundle.affiliationPriority.indexOf(checkAffil);
            if (checkIndex > currentPriority) {
                currentPriority = checkIndex;
                const testAffiliation = dataBundle.affiliationPriority[checkIndex];
                if (dataBundle[dataSet][testAffiliation] &&
                    dataBundle[dataSet][testAffiliation].length > 0) {
                    chosenAffiliation = dataBundle.affiliationPriority[checkIndex];
                }
            }
        }
    } else if (dataBundle.affiliationPriority &&
        dataBundle.affiliationPriority.length === 1) {
        chosenAffiliation = dataBundle.affiliationPriority[0];
    } else {
        chosenAffiliation = this.defaultAffiliation;
    }

    return dataBundle[dataSet][chosenAffiliation];
}

  extractUserInfo(affiliation) {
    const siteId = this.configService.getValue('siteId');
    // Set default affiliation as fallback
    const affiliationList = [this.defaultAffiliation];
    // Create affiliation array and standardize default affiliation
    if (affiliation !== undefined &&
       affiliation[siteId] !== undefined &&
       affiliation[siteId].storedAffiliations !== undefined &&
       affiliation[siteId].storedAffiliations.length !== 0
    ) {
        for (const key in affiliation[siteId].storedAffiliations) {
            if (affiliation[siteId].storedAffiliations[key] !== 'STD_GST') {
                affiliationList.push(affiliation[siteId].storedAffiliations[key]);
            }
        }
    }

    // TODO: add in dynamic profile function instead of static text
    this.currentUserInfo = {
        affiliations: affiliationList,
        profile: this.defaultProfile
    };

    return this.currentUserInfo;
  }

}
